
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  BarChart3,
  Vote,
  Plus,
  Play,
  Square,
  Users,
  TrendingUp,
  AlertCircle,
  Settings,
  Trash2
} from 'lucide-react';
import { usePollStore, useActivePolls, usePollStats, useUserVote } from '@/lib/stores/polls/store';
import { useAuthStore } from '@/lib/stores/auth';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';

interface PollingSectionProps {
  streamId: string;
  isLive?: boolean;
  className?: string;
}

export function PollingSection({ streamId, isLive = false, className }: PollingSectionProps) {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { user } = useAuthStore();
  const {
    polls,
    error,
    votingPollIds,
    loadPolls,
    votePoll,
    removeVote,
    clearError,
    handleRealtimeVoteUpdate,
  } = usePollStore();

  const activePolls = useActivePolls(streamId);
  const stats = usePollStats(streamId);

  // Subscribe to real-time poll updates via Durable Objects
  const handleMessage = useCallback((message: any) => {
    if (message.type === 'poll_vote_update' && message.pollId && message.optionIndex !== undefined) {
      handleRealtimeVoteUpdate(message.pollId.toString(), message.optionIndex, message.voteCount);
    } else if (message.type === 'poll_created' && message.poll) {
      // Reload polls to get the new poll
      loadPolls(streamId);
    } else if (message.type === 'poll_ended' && message.pollId) {
      // Reload polls to get updated status
      loadPolls(streamId);
    }
  }, [streamId, handleRealtimeVoteUpdate, loadPolls]);

  const { isConnected } = useDurableObjectChannel({
    channel: `stream:polls:${streamId}`,
    enabled: !!streamId && isLive,
    onMessage: handleMessage,
  });

  // Load polls on mount
  useEffect(() => {
    loadPolls(streamId);
  }, [streamId, loadPolls]);

  const currentPoll = activePolls[0]; // Show first active poll
  const userVote = currentPoll ? useUserVote(currentPoll.id) : null;
  const isVotingCurrentPoll = currentPoll ? votingPollIds.has(currentPoll.id) : false;

  const handleVote = async () => {
    if (!currentPoll || selectedOptions.size === 0) return;

    const optionIds = Array.from(selectedOptions);
    await votePoll(currentPoll.id, optionIds);
    setSelectedOptions(new Set());
  };

  const handleRemoveVote = async () => {
    if (!currentPoll) return;
    await removeVote(currentPoll.id);
  };

  const handleOptionSelect = (optionId: string, checked: boolean) => {
    if (checked) {
      if (currentPoll?.allowMultiple) {
        setSelectedOptions(prev => new Set([...prev, optionId]));
      } else {
        setSelectedOptions(new Set([optionId]));
      }
    } else {
      setSelectedOptions(prev => {
        const newSet = new Set(prev);
        newSet.delete(optionId);
        return newSet;
      });
    }
  };

  if (!isLive && activePolls.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <Vote className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-medium mb-2">No Active Polls</h3>
            <p className="text-sm text-muted-foreground">
              Polls will appear here when available during live streams.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Vote className="h-5 w-5" />
              Live Polling
            </CardTitle>
            <CardDescription>
              Participate in community polls and see real-time results
            </CardDescription>
          </div>

          {/* Poll Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold">{stats.activePolls}</div>
              <div className="text-muted-foreground">Active</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">{stats.totalVotes}</div>
              <div className="text-muted-foreground">Total Votes</div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              className="ml-auto"
            >
              Dismiss
            </Button>
          </Alert>
        )}

        {/* Active Poll */}
        {currentPoll && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant="destructive" className="animate-pulse">
                <Play className="h-3 w-3 mr-1" />
                LIVE POLL
              </Badge>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                {currentPoll.totalVotes} votes
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">{currentPoll.question}</h3>

              {/* Voting Options */}
              {!userVote ? (
                <div className="space-y-3">
                  {currentPoll.options.map((option) => (
                    <div key={option.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={option.id}
                        checked={selectedOptions.has(option.id)}
                        onCheckedChange={(checked) => handleOptionSelect(option.id, checked as boolean)}
                      />
                      <label
                        htmlFor={option.id}
                        className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {option.text}
                      </label>
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      onClick={handleVote}
                      disabled={selectedOptions.size === 0 || isVotingCurrentPoll}
                      size="sm"
                      className="flex-1"
                    >
                      {isVotingCurrentPoll ? (
                        <LoadingSpinner className="h-4 w-4 mr-2" />
                      ) : (
                        <Vote className="h-4 w-4 mr-2" />
                      )}
                      Vote
                    </Button>

                    {currentPoll.isAnonymous && (
                      <Badge variant="outline" className="text-xs">
                        Anonymous
                      </Badge>
                    )}
                  </div>
                </div>
              ) : (
                /* Results View */
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <span>Live Results</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveVote}
                      disabled={isVotingCurrentPoll}
                    >
                      Change Vote
                    </Button>
                  </div>

                  {currentPoll.options
                    .sort((a, b) => b.votes - a.votes)
                    .map((option, index) => (
                      <div key={option.id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {index === 0 && option.votes > 0 && (
                              <Badge variant="default" className="text-xs">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                Leading
                              </Badge>
                            )}
                            <span className="text-sm font-medium">{option.text}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{option.votes} votes</span>
                            <span>({(option.percentage ?? 0).toFixed(1)}%)</span>
                          </div>
                        </div>
                        <Progress value={option.percentage ?? 0} className="h-2" />
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Poll History */}
        {polls.filter(p => p.status === 'ended').length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium text-muted-foreground">Recent Polls</h4>
            <div className="space-y-3">
              {polls
                .filter(p => p.status === 'ended')
                .slice(0, 3)
                .map((poll) => (
                  <div key={poll.id} className="p-4 border rounded-lg bg-muted/20">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-sm">{poll.question}</h5>
                      <Badge variant="secondary" className="text-xs">
                        {poll.totalVotes} votes
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      {poll.options
                        .sort((a, b) => b.votes - a.votes)
                        .slice(0, 3)
                        .map((option) => (
                          <div key={option.id} className="flex items-center justify-between text-xs">
                            <span className="truncate flex-1 mr-2">{option.text}</span>
                            <div className="flex items-center gap-2">
                              <Progress value={option.percentage ?? 0} className="w-16 h-1" />
                              <span className="text-muted-foreground w-12 text-right">
                                {(option.percentage ?? 0).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* No active polls message */}
        {!currentPoll && isLive && (
          <div className="text-center py-8 text-muted-foreground">
            <Vote className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="font-medium mb-2">Waiting for Poll</h3>
            <p className="text-sm">
              A poll will appear here when the host starts one.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Poll Creation Component (Admin)
interface PollCreatorProps {
  streamId: string;
  className?: string;
}

export function PollCreator({ streamId, className }: PollCreatorProps) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [duration, setDuration] = useState<number | undefined>();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { createPoll, startPoll, creatingPoll } = usePollStore();

  const handleAddOption = () => {
    if (options.length < 10) {
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    setOptions(options.map((opt, i) => i === index ? value : opt));
  };

  const handleCreatePoll = async () => {
    if (!question.trim()) return;

    const validOptions = options.filter(opt => opt.trim());
    if (validOptions.length < 2) return;

    await createPoll(streamId, question, validOptions, {
      isAnonymous,
      allowMultiple,
      duration,
    });

    // Reset form
    setQuestion('');
    setOptions(['', '']);
    setIsAnonymous(true);
    setAllowMultiple(false);
    setDuration(undefined);
  };

  const handleQuickPoll = async (type: 'yesno' | 'rating' | 'multiple') => {
    let pollQuestion = '';
    let pollOptions: string[] = [];

    switch (type) {
      case 'yesno':
        pollQuestion = 'Quick Yes/No Question';
        pollOptions = ['Yes', 'No'];
        break;
      case 'rating':
        pollQuestion = 'How would you rate this?';
        pollOptions = ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'];
        break;
      case 'multiple':
        pollQuestion = 'Quick Multiple Choice';
        pollOptions = ['Option A', 'Option B', 'Option C', 'Option D'];
        break;
    }

    await createPoll(streamId, pollQuestion, pollOptions, {
      isAnonymous: true,
      allowMultiple: false,
    });
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Create Poll
        </CardTitle>
        <CardDescription>
          Engage your audience with live polls
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Quick Poll Templates */}
        <div>
          <h4 className="text-sm font-medium mb-3">Quick Polls</h4>
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickPoll('yesno')}
              disabled={creatingPoll}
            >
              Yes/No
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickPoll('rating')}
              disabled={creatingPoll}
            >
              Rating
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickPoll('multiple')}
              disabled={creatingPoll}
            >
              Multiple Choice
            </Button>
          </div>
        </div>

        {/* Custom Poll Creator */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              <Settings className="h-4 w-4 mr-2" />
              Create Custom Poll
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Custom Poll</DialogTitle>
              <DialogDescription>
                Create a custom poll to engage your audience
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Question */}
              <div>
                <label className="text-sm font-medium">Question</label>
                <Input
                  placeholder="Enter your poll question..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Options */}
              <div>
                <label className="text-sm font-medium">Options</label>
                <div className="space-y-2 mt-1">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                      />
                      {options.length > 2 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveOption(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {options.length < 10 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddOption}
                    className="mt-2"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Option
                  </Button>
                )}
              </div>

              {/* Settings */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="anonymous"
                    checked={isAnonymous}
                    onCheckedChange={(checked) => setIsAnonymous(checked === true)}
                  />
                  <label htmlFor="anonymous" className="text-sm">
                    Anonymous voting
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="multiple"
                    checked={allowMultiple}
                    onCheckedChange={(checked) => setAllowMultiple(checked === true)}
                  />
                  <label htmlFor="multiple" className="text-sm">
                    Allow multiple selections
                  </label>
                </div>

                <div>
                  <label className="text-sm font-medium">Duration (optional)</label>
                  <Input
                    type="number"
                    placeholder="Duration in seconds"
                    value={duration || ''}
                    onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                    className="mt-1"
                    min="30"
                    max="3600"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreatePoll}
                  disabled={!question.trim() || options.filter(o => o.trim()).length < 2 || creatingPoll}
                >
                  {creatingPoll ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
                  Create Poll
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// Poll Management Component (Admin)
interface PollManagerProps {
  streamId: string;
  className?: string;
}

export function PollManager({ streamId, className }: PollManagerProps) {
  const [selectedPolls, setSelectedPolls] = useState<Set<string>>(new Set());

  const { polls, startPoll, endPoll, deletePoll, bulkDeletePolls, endingPollIds, deletingPollIds } = usePollStore();
  const streamPolls = polls.filter(p => p.streamId === streamId);

  const handleSelectPoll = (pollId: string, checked: boolean) => {
    setSelectedPolls(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(pollId);
      } else {
        newSet.delete(pollId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedPolls.size === 0) return;
    await bulkDeletePolls(Array.from(selectedPolls));
    setSelectedPolls(new Set());
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Poll Management
            </CardTitle>
            <CardDescription>
              Manage polls for this stream
            </CardDescription>
          </div>

          {selectedPolls.size > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
              Delete Selected ({selectedPolls.size})
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {streamPolls.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No polls created yet</p>
              <p className="text-sm">Create your first poll to engage the audience</p>
            </div>
          ) : (
            streamPolls.map((poll) => (
              <div key={poll.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedPolls.has(poll.id)}
                    onCheckedChange={(checked) => handleSelectPoll(poll.id, checked as boolean)}
                  />

                  <div className="flex-1">
                    <h4 className="font-medium">{poll.question}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant={
                          poll.status === 'active' ? 'destructive' :
                          poll.status === 'ended' ? 'secondary' : 'outline'
                        }
                        className="text-xs"
                      >
                        {poll.status}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {poll.totalVotes} votes
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {poll.options.length} options
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {poll.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startPoll(poll.id)}
                      disabled={endingPollIds.has(poll.id) || deletingPollIds.has(poll.id)}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Start
                    </Button>
                  )}

                  {poll.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => endPoll(poll.id)}
                      disabled={endingPollIds.has(poll.id) || deletingPollIds.has(poll.id)}
                    >
                      <Square className="h-4 w-4 mr-1" />
                      End
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deletePoll(poll.id)}
                    disabled={deletingPollIds.has(poll.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
